// Frontend types for JamPack'd Rush'd membership system

export interface RushdMembershipStatus {
  isMember: boolean;
  isActive: boolean;
  plan?: 'monthly' | 'yearly';
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd?: boolean;
  usageThisPeriod?: number;
  usageLimit?: number;
  nextBillingDate?: Date;
}

export interface RushdPricingQuote {
  regularPrice: number;
  memberPrice?: number;
  savings?: number;
  isMember: boolean;
  isEligible: boolean;
  reason?: string;
  usageRemaining?: number;
}

export interface RushdPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  stripePriceId: string;
}

export interface RushdCheckoutSession {
  sessionId: string;
  url: string;
}

export interface RushdBillingPortalSession {
  url: string;
}

export interface RushdSettings {
  programActive: boolean;
  memberExpressPriceMode: 'percentage_off' | 'flat_price';
  memberRateValue: number;
  minOrderValueEnabled: boolean;
  minOrderValue?: number;
  monthlyUsageLimitEnabled: boolean;
  monthlyUsageLimit?: number;
  membersOnlyDiscountsEnabled: boolean;
  randomFreeTreatsEnabled: boolean;
  priorityPackingEnabled: boolean;
}

export interface RushdErrorState {
  type: 'NETWORK_ERROR' | 'AUTH_ERROR' | 'VALIDATION_ERROR' | 'UNKNOWN_ERROR';
  message: string;
  retryable: boolean;
  timestamp: Date;
}

export interface RushdMembershipContextType {
  membershipStatus: RushdMembershipStatus | null;
  loading: boolean;
  error: string | null;
  errorState?: RushdErrorState | null;
  refreshMembership: (forceRefresh?: boolean) => Promise<void>;
  createCheckoutSession: (plan: 'monthly' | 'yearly', successUrl: string, cancelUrl: string) => Promise<RushdCheckoutSession>;
  createBillingPortalSession: (returnUrl: string) => Promise<RushdBillingPortalSession>;
  getExpressPricingQuote: (cartItems: any[], shippingAddress: any, cartSubtotal: number) => Promise<RushdPricingQuote>;
  retryLastOperation?: () => Promise<void>;
  clearError?: () => void;
  retryCount?: number;
}

export interface RushdApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    message: string;
    details?: string;
  };
}

// Plan configurations for display
export const RUSHD_PLAN_CONFIG = {
  monthly: {
    id: 'monthly',
    name: 'Monthly',
    price: 9.99,
    currency: 'USD',
    interval: 'month' as const,
    description: 'Perfect for trying out Rush\'d benefits',
    badge: null,
  },
  yearly: {
    id: 'yearly',
    name: 'Yearly',
    price: 59.99,
    currency: 'USD',
    interval: 'year' as const,
    description: 'Best value - Save over 50%!',
    badge: 'Best Value' as const,
    monthlySavings: (12 * 9.99) - 59.99, // $59.89 savings
  },
} as const;

// Rush'd member perks for display
export const RUSHD_PERKS = [
  {
    id: 'priority_packing',
    name: 'Priority Packing and Dispatch',
    description: 'Your orders are packed and shipped ahead of regular orders',
    icon: 'Package',
  },
  {
    id: 'members_discounts',
    name: 'Members-Only Discounts',
    description: 'Access to exclusive discount codes not available to regular customers',
    icon: 'Tag',
  },
  {
    id: 'free_treats',
    name: 'Random Free Treats',
    description: 'Surprise bonus items added to your packages for extra delight',
    icon: 'Gift',
  },
] as const;

// Utility type for Rush'd eligibility reasons
export type RushdIneligibilityReason = 
  | 'not_member'
  | 'inactive_membership'
  | 'program_inactive'
  | 'min_order_not_met'
  | 'usage_limit_reached'
  | 'not_signed_in';

// Helper function to get user-friendly eligibility message
export const getRushdEligibilityMessage = (reason?: string): string => {
  if (!reason) return '';
  
  const messages: Record<string, string> = {
    'not_member': 'Join Rush\'d to access member pricing',
    'inactive_membership': 'Rush\'d membership is not active',
    'program_inactive': 'Rush\'d program is temporarily unavailable',
    'min_order_not_met': 'Minimum order value required for member pricing',
    'usage_limit_reached': 'Monthly Rush\'d usage limit reached',
    'not_signed_in': 'Sign in to access Rush\'d member pricing',
  };
  
  return messages[reason] || reason;
};

// API endpoints
export const RUSHD_API_ENDPOINTS = {
  membership: (userId: string) => `/rushd/membership/${userId}`,
  createCheckoutSession: '/rushd/create-checkout-session',
  createBillingPortalSession: '/rushd/create-billing-portal-session',
  quoteExpressShipping: '/rushd/quote-express-shipping',
  settings: '/rushd/settings',
  plans: '/rushd/plans',
} as const;